import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { chatAttach, deleteChatAttachment, uploadGeneral } from '../../api';
import { handleGlobalApiCalls } from '../../helpers/asyncActions';
import { getImageUrl } from '../../helpers/images';
import { useGlobalContext } from '../../stores/global';
import Button from '../core/Button';
import FormElement from '../core/FormElement';
import Modal from '../Modals/Modal';
import YesNoPrompt from '../Modals/YesNoPrompt';
import styles from './DocumentsModal.module.scss';

interface Props {
  open: boolean;
  onClose?: () => void;
  chatObj: any;
  me: any;
  onFileUploaded: () => void;
  onFileDeleted: () => Promise<void>;
}

const noDocumentsEl = <p className={styles.ph}>Sin documentos</p>;

export default function DocumentsModal({
  open,
  onClose,
  chatObj,
  me,
  onFileUploaded,
  onFileDeleted,
}: Props) {
  if (!open) return null;

  const { dispatch, isLoading } = useGlobalContext();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const { register, handleSubmit, errors } = useForm();
  const [attachToDelete, setAttachToDelete] = useState<string>();
  const { receiverName, receiverUserId } = chatObj || {};

  const handleDocumentUpload = (fileList: FileList, name: string) => {
    const file = fileList[0];

    if (!file) return;

    const bodyFormData = new FormData();
    bodyFormData.append('file', file);

    return handleGlobalApiCalls(dispatch, isLoading, null, () =>
      uploadGeneral(bodyFormData).then((response) =>
        chatAttach(chatObj.id, {
          ...response.data.data,
          user: me.id,
          name,
        })
      )
    );
  };

  const handleFormSubmit = async ({ fileList, name }: any) => {
    await handleDocumentUpload(fileList, name);
    setShowUploadModal(false);
    onFileUploaded();
  };

  const removeAttach = async (attach: any) => {
    return handleGlobalApiCalls(dispatch, isLoading, null, () =>
      deleteChatAttachment(chatObj.id, attach._id).finally(async () => {
        await onFileDeleted();
        setAttachToDelete(undefined);
      })
    );
  };

  const documents = chatObj.attaches?.reduce(
    (result: any, el: any) => {
      if (el.user === me.id) result.me.push(el);
      if (el.user === receiverUserId) result.otherGuy.push(el);
      return result;
    },
    { me: [], otherGuy: [] }
  );

  return (
    <>
      <Modal
        open={open}
        title="Documentos Compartidos"
        onClose={onClose}
        closeBtn
      >
        <p>Documentos compartidos en el chat</p>
        <h2 className={styles.title}>Mis documentos</h2>
        {documents.me.length === 0
          ? noDocumentsEl
          : documents.me.map((attach: any, idx: any) => (
              <p key={idx} className={styles.attach}>
                <a href={getImageUrl(attach)} target="_blank">
                  {attach.name}
                </a>
                <a
                  className={styles.delete}
                  onClick={() => setAttachToDelete(attach)}
                >
                  Eliminar
                </a>
              </p>
            ))}

        <h2 className={styles.title}>Documentos de {receiverName}</h2>
        {documents.otherGuy.length === 0
          ? noDocumentsEl
          : documents.otherGuy.map((attach: any, idx: any) => (
              <p key={idx} className={styles.attach}>
                <a href={getImageUrl(attach)} target="_blank">
                  {attach.name}
                </a>
              </p>
            ))}

        <Button
          style={{ marginTop: 30 }}
          onClick={() => setShowUploadModal(true)}
          label="Subir Documento"
        />
      </Modal>

      <Modal
        open={showUploadModal}
        title="Subir documento"
        onClose={() => setShowUploadModal(false)}
        closeBtn
      >
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <FormElement
            elementType="text"
            name="name"
            placeholder="Nombre del documento"
            variant="outlined"
            inputRef={register({ required: true })}
            autoComplete="off"
            error={errors.name}
          />
          <input
            type="file"
            name="fileList"
            ref={register({ required: true })}
            style={{ display: 'block', marginBottom: 10 }}
          />
          {errors.fileList ? (
            <p className={styles.error}>Selecciona un documento</p>
          ) : null}
          <Button type="submit" label="SUBIR" />
        </form>
      </Modal>

      <YesNoPrompt
        open={Boolean(attachToDelete)}
        title="Eliminar documento"
        text="Seguro que desea eliminar este documento?"
        onClose={() => setAttachToDelete(undefined)}
        onYes={() => removeAttach(attachToDelete)}
      />
    </>
  );
}
